import React, { useContext } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import {
  ALLOWED_CRM_ENTITY_NAMES,
  FlattenedActivityEntityRow,
  SalesActivityTypeCount,
  aggregateActivityByEntityType,
} from "./activitiesReportService";
import {
  CATEGORICAL_PALETTE,
  renderTooltipCard,
  renderTooltipEntityHeader,
  renderTooltipStatusRow,
} from "./activityChartPalette";
import { AuthContext } from "../../../../context/authcontext";
import { getLifecycleEntityLabel } from "../../../../utils/lifecycleUtils";

interface ActivityEntityBreakdownChartProps {
  rows: FlattenedActivityEntityRow[];
  types: SalesActivityTypeCount[];
  selectedTypeId: number | null;
  selectedStatusId: number | null;
  onSelectType: (typeId: number) => void;
  onSelectStatus: (statusId: number) => void;
}

interface Segment {
  label: string;
  total: number;
  entityCounts: Record<string, number>;
  typeId?: number;
  statusId?: number;
}

const ActivityEntityBreakdownChart: React.FC<ActivityEntityBreakdownChartProps> = ({
  rows,
  types,
  selectedTypeId,
  selectedStatusId,
  onSelectType,
  onSelectStatus,
}) => {
  const { lifecycleTypeNames } = useContext(AuthContext);
  const entityLabels = ALLOWED_CRM_ENTITY_NAMES.map((entity) => getLifecycleEntityLabel(entity, lifecycleTypeNames));
  if (rows.length === 0) {
    return (
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ height: "220px" }}
      >
        <div className="text-center">
          <i className="ti ti-chart-bar fs-1 text-muted mb-3" />
          <p className="text-muted mb-0">
            No activity-by-entity data for the selected filters
          </p>
        </div>
      </div>
    );
  }

  const selectedType = types.find(
    (t) => t.crm_sales_activity_type_id === selectedTypeId
  );

  // Default: one stacked segment per activity type (statuses summed).
  // Drilled into a type: one segment per that type's status instead —
  // same chart, just re-sliced, so the number of segments stays bounded
  // either way (types, or one type's handful of statuses).
  const segments: Segment[] = selectedType
    ? rows
        .filter((row) => {
          if (row.type !== selectedType.crm_sales_activity_type_display_name) return false;
          if (selectedStatusId) {
            const status = selectedType.statuses?.find(
              (s) => s.crm_sales_activity_type_status_id === selectedStatusId
            );
            return !status || row.status === status.crm_sales_activity_type_status_display_name;
          }
          return true;
        })
        .map((row) => {
          const status = selectedType.statuses?.find(
            (s) => s.crm_sales_activity_type_status_display_name === row.status
          );
          const rowSum = Object.values(row.entityCounts).reduce((a, b) => a + (b || 0), 0);
          return {
            label: row.status,
            total: rowSum > 0 ? rowSum : (status?.counts ?? 0),
            entityCounts: row.entityCounts,
            statusId: status?.crm_sales_activity_type_status_id,
          };
        })
    : aggregateActivityByEntityType(rows).map((row) => {
        const type = types.find(
          (t) => t.crm_sales_activity_type_display_name === row.type
        );
        return {
          label: row.type,
          total: type?.counts ?? 0,
          entityCounts: row.entityCounts,
          typeId: type?.crm_sales_activity_type_id,
        };
      });

  segments.sort((a, b) => b.total - a.total);

  const series = segments.map((segment) => ({
    name: segment.label,
    data: ALLOWED_CRM_ENTITY_NAMES.map(
      (entity) => segment.entityCounts[entity] || 0
    ),
  }));

  const colors = segments.map(
    (_, index) => CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length]
  );

  const options: ApexOptions = {
    chart: {
      type: "bar",
      height: 380,
      stacked: true,
      toolbar: { show: false },
      // Keeps this chart mounted across drill-downs and just gets fresh
      // series/options in place (see index.tsx) — disabling animations
      // avoids ApexCharts' internal animation timers colliding with a
      // sibling chart's remount in the same click-driven state cascade.
      animations: { enabled: false },
      events: {
        dataPointSelection: (_event, _ctx, config) => {
          const segment = segments[config.seriesIndex];
          if (!segment) return;
          // Deferred: this can remount a sibling chart (the status donut)
          // whose `key` depends on the selected type/status. Doing that
          // synchronously, still inside ApexCharts' own click handling for
          // *this* chart, races that stack and throws.
          if (segment.typeId) {
            const typeId = segment.typeId;
            setTimeout(() => onSelectType(typeId), 0);
          } else if (segment.statusId) {
            const statusId = segment.statusId;
            setTimeout(() => onSelectStatus(statusId), 0);
          }
        },
      },
    },
    colors,
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        type: "horizontal",
        shadeIntensity: 0.25,
        opacityFrom: 1,
        opacityTo: 0.88,
      },
    },
    dataLabels: {
      enabled: true,
      style: { fontSize: "11px", fontWeight: 600, colors: ["#fff"] },
      formatter: (val: number) => (typeof val === "number" && val > 0 ? String(val) : ""),
    },
    // No stroke: a per-segment border made each stacked slice read as a
    // separate disconnected piece instead of one bar that's just
    // different colors along its length.
    stroke: { width: 0 },
    xaxis: {
      categories: entityLabels,
      labels: { formatter: (val) => Math.round(Number(val)).toString() },
    },
    yaxis: {
      labels: { style: { fontWeight: 600 } },
    },
    grid: {
      borderColor: "#e1e0d9",
      strokeDashArray: 4,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } },
    },
    legend: {
      show: true,
      position: "bottom",
      fontWeight: 600,
      markers: { size: 7 },
    },
    tooltip: {
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        const segment = segments[seriesIndex];
        const entity = ALLOWED_CRM_ENTITY_NAMES[dataPointIndex];
        const value = series[seriesIndex][dataPointIndex];
        const swatch = w.globals.colors[seriesIndex];
        const header = renderTooltipEntityHeader(value, segment.label, entityLabels[dataPointIndex], swatch);

        if (selectedType) {
          // Drilled view: segments are already statuses, the finest grain
          // there is — nothing further to break down.
          return renderTooltipCard(header);
        }

        // Default view: segments are types — show that type's status
        // split for this specific entity, so hovering answers "which
        // statuses make up this type's count here?" without a click.
        const statusRows = rows
          .filter((row) => row.type === segment.label)
          .map((row) =>
            renderTooltipStatusRow(row.status, row.entityCounts[entity] || 0)
          )
          .join("");
        return renderTooltipCard(header + statusRows);
      },
    },
    states: {
      hover: { filter: { type: "darken" } },
    },
  };

  return (
    <>
      <Chart options={options} series={series} type="bar" height={380} />
      <p className="text-muted fs-12 mb-0 mt-2">
        <i className="ti ti-info-circle me-1" />
        {selectedType
          ? "Click a status segment to filter by it too."
          : "Click a bar segment to drill into that activity type's status breakdown."}
      </p>
    </>
  );
};

export default ActivityEntityBreakdownChart;
